import React, { useMemo } from 'react';
import DOMPurify from 'dompurify';
import katex from 'katex';

import {
  findInlineImage,
  getColumnLayout,
  getTableCellOwnership,
  getTableCellStyle,
  normalizeTableShape,
} from './contentLayout';
import { TABLE_ID_PATTERN } from './bookTableAnchor';
import { convertLegacyTagsToHtml } from './legacyContent';
import {
  decodeEquationLatex,
  normalizeBookEquation,
} from './bookEquationCodec';
import './ContentLayout.css';


const ALLOWED_STYLE_PROPERTIES = new Set([
  'background-color',
  'color',
  'font-family',
  'font-size',
  'font-style',
  'font-weight',
  'text-align',
  'text-decoration',
]);

const INDENT_TAGS = new Set([
  'P',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'LI',
]);

const sanitizeBookIndents = (root) => {
  root.querySelectorAll('[data-book-indent]').forEach((element) => {
    const value = Number(element.getAttribute('data-book-indent'));
    if (
      !INDENT_TAGS.has(element.tagName) ||
      !Number.isInteger(value) ||
      value < 0 ||
      value > 8
    ) {
      element.removeAttribute('data-book-indent');
    }
  });
};

const isSafeMediaUrl = (value) => {
  if (typeof value !== 'string' || !value.trim()) return false;
  const candidate = value.trim().replace(/\\/g, '/');
  if (/^(javascript|data|vbscript|file):/i.test(candidate)) return false;
  try {
    const parsed = new URL(candidate, window.location.origin);
    return ['http:', 'https:'].includes(parsed.protocol);
  } catch {
    return false;
  }
};

const sanitizeTextStyles = (root) => {
  root.querySelectorAll('[style]').forEach((element) => {
    const safeDeclarations = [];
    String(element.getAttribute('style') || '')
      .split(';')
      .map((part) => part.trim())
      .filter(Boolean)
      .forEach((declaration) => {
        const separator = declaration.indexOf(':');
        if (separator === -1) return;
        const property = declaration.slice(0, separator).trim().toLowerCase();
        const value = declaration.slice(separator + 1).trim();
        if (
          ALLOWED_STYLE_PROPERTIES.has(property) &&
          !/(url\s*\(|expression\s*\(|javascript:|@import)/i.test(value)
        ) {
          safeDeclarations.push(`${property}: ${value}`);
        }
      });
    if (safeDeclarations.length) {
      element.setAttribute('style', safeDeclarations.join('; '));
    } else {
      element.removeAttribute('style');
    }
  });
};

const renderBookEquations = (root) => {
  root
    .querySelectorAll('.katex, .katex-display, .katex-html, .katex-mathml')
    .forEach((submittedRendererNode) => submittedRendererNode.remove());

  root
    .querySelectorAll(
      'span[data-book-equation="true"], div[data-book-equation="true"]'
    )
    .forEach((anchor) => {
      const display = anchor.getAttribute('data-equation-display');
      const expectedTag = display === 'inline' ? 'SPAN' : 'DIV';
      if (
        !['inline', 'block'].includes(display) ||
        anchor.tagName !== expectedTag ||
        anchor.childNodes.length > 0
      ) {
        anchor.remove();
        return;
      }

      try {
        const latex = decodeEquationLatex(
          anchor.getAttribute('data-equation-latex')
        );
        const equation = normalizeBookEquation({ latex, display });
        const rendered = katex.renderToString(equation.latex, {
          displayMode: equation.display === 'block',
          throwOnError: true,
          trust: false,
          strict: 'error',
          maxSize: 10,
          maxExpand: 100,
          output: 'htmlAndMathml',
        });

        [...anchor.attributes].forEach((attribute) => {
          anchor.removeAttribute(attribute.name);
        });
        anchor.className = `book-equation book-equation--${display}`;
        anchor.innerHTML = rendered;
      } catch {
        anchor.remove();
      }
    });
};

const createSafeTableElement = (table) => {
  const rawUrl = table?.url || table?.filepath || '';
  const isSnapshot =
    table?.is_snapshot === true || /\.(png|jpe?g|gif|webp)$/i.test(rawUrl);
  const wrapper = document.createElement('div');
  wrapper.className = 'book-table-scroll';

  if (isSnapshot) {
    if (!isSafeMediaUrl(rawUrl)) return null;
    const image = document.createElement('img');
    image.src = rawUrl;
    image.alt = String(table?.caption || table?.title || 'Tableau');
    image.loading = 'lazy';
    image.className = 'book-table-snapshot';
    wrapper.appendChild(image);
  } else {
    const shaped = normalizeTableShape(table);
    const layout = getColumnLayout(shaped);
    const ownership = getTableCellOwnership(shaped);
    const tableElement = document.createElement('table');
    tableElement.className = 'book-content-table';
    if (layout.unit === 'px') {
      const pixelWidth = layout.values.reduce(
        (sum, value) => sum + Number(value),
        0
      );
      tableElement.style.width = `${pixelWidth}px`;
      tableElement.style.minWidth = `${pixelWidth}px`;
    }

    const colgroup = document.createElement('colgroup');
    layout.values.forEach((width) => {
      const column = document.createElement('col');
      column.style.width = `${width}${layout.unit === 'px' ? 'px' : '%'}`;
      colgroup.appendChild(column);
    });
    tableElement.appendChild(colgroup);

    const body = document.createElement('tbody');
    shaped.grid.forEach((row, rowIndex) => {
      const tableRow = document.createElement('tr');
      row.forEach((value, columnIndex) => {
        const owner = ownership.get(`${rowIndex}:${columnIndex}`);
        if (
          owner &&
          (owner.row !== rowIndex || owner.column !== columnIndex)
        ) {
          return;
        }
        const cell = document.createElement('td');
        if (owner?.rowspan > 1) {
          cell.setAttribute('rowspan', String(owner.rowspan));
        }
        if (owner?.colspan > 1) {
          cell.setAttribute('colspan', String(owner.colspan));
        }
        const cellStyle = getTableCellStyle(shaped, {
          row: rowIndex,
          column: columnIndex,
        });
        if (cellStyle?.background_color) {
          cell.style.backgroundColor = cellStyle.background_color;
        }
        const text = document.createElement('span');
        text.className = [
          'book-table-cell-text',
          cellStyle?.text_direction === 'vertical-up'
            ? 'book-table-cell-text--vertical-up'
            : '',
        ]
          .filter(Boolean)
          .join(' ');
        text.textContent = String(value ?? '');
        cell.appendChild(text);
        tableRow.appendChild(cell);
      });
      body.appendChild(tableRow);
    });
    tableElement.appendChild(body);
    wrapper.appendChild(tableElement);
  }

  const title = table?.caption || table?.title;
  if (title) {
    const caption = document.createElement('p');
    caption.className = 'book-table-caption';
    caption.textContent = String(title);
    wrapper.appendChild(caption);
  }
  return wrapper;
};

const renderBookTables = (root, tables) => {
  const tableById = new Map(
    (Array.isArray(tables) ? tables : [])
      .filter(
        (table) =>
          table &&
          typeof table === 'object' &&
          TABLE_ID_PATTERN.test(table.id || '')
      )
      .map((table) => [table.id, table])
  );

  root.querySelectorAll('[data-book-table-id]').forEach((anchor) => {
    const tableId = anchor.getAttribute('data-book-table-id');
    const table = tableById.get(tableId);
    if (
      anchor.tagName !== 'DIV' ||
      anchor.attributes.length !== 1 ||
      anchor.childNodes.length !== 0 ||
      !TABLE_ID_PATTERN.test(tableId || '') ||
      !table
    ) {
      anchor.remove();
      return;
    }
    const renderedTable = createSafeTableElement(table);
    if (renderedTable) {
      anchor.replaceWith(renderedTable);
    } else {
      anchor.remove();
    }
  });
};

export const buildSafeRichHtml = (
  content,
  images,
  contentLanguage = 'fr',
  tables = []
) => {
  const sanitized = DOMPurify.sanitize(convertLegacyTagsToHtml(content), {
    ALLOWED_TAGS: [
      'p',
      'br',
      'strong',
      'b',
      'em',
      'i',
      'u',
      's',
      'a',
      'sub',
      'sup',
      'span',
      'div',
      'blockquote',
      'ul',
      'ol',
      'li',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'article',
      'figure',
      'figcaption',
    ],
    ALLOWED_ATTR: [
      'class',
      'style',
      'value',
      'data-callout',
      'data-type',
      'data-book-image-id',
      'data-book-indent',
      'data-book-equation',
      'data-equation-display',
      'data-equation-latex',
      'data-book-table-id',
      'href',
      'target',
      'rel',
    ],
  });
  const root = document.createElement('div');
  root.innerHTML = sanitized;
  sanitizeTextStyles(root);
  sanitizeBookIndents(root);
  renderBookEquations(root);
  renderBookTables(root, tables);

  root
    .querySelectorAll('figure[data-book-image-id]')
    .forEach((anchor) => {
      const imageId = anchor.getAttribute('data-book-image-id');
      const image = findInlineImage(images, imageId, contentLanguage);
      if (!image || !isSafeMediaUrl(image.url)) {
        anchor.remove();
        return;
      }

      const placement = image.placement || {};
      const align = ['left', 'center', 'right', 'full'].includes(placement.align)
        ? placement.align
        : 'center';
      const width = placement.width || {};
      const widthValue = Number(width.value);
      const unit = width.unit === 'px' ? 'px' : '%';
      if (!Number.isFinite(widthValue) || widthValue <= 0) {
        anchor.remove();
        return;
      }

      anchor.replaceChildren();
      anchor.className = [
        'book-inline-image',
        `book-inline-image--${align}`,
        placement.wrap === 'around' ? 'book-inline-image--wrap' : '',
      ]
        .filter(Boolean)
        .join(' ');
      anchor.style.width = `${widthValue}${unit}`;
      anchor.style.maxWidth = '100%';

      const imageElement = document.createElement('img');
      imageElement.src = image.url;
      imageElement.alt = String(image.caption || '');
      imageElement.loading = 'lazy';
      imageElement.setAttribute('data-book-image-id', imageId);
      anchor.appendChild(imageElement);

      if (image.caption) {
        const caption = document.createElement('figcaption');
        caption.textContent = String(image.caption);
        anchor.appendChild(caption);
      }
    });

  return DOMPurify.sanitize(root.innerHTML, {
    ALLOWED_TAGS: [
      'p',
      'br',
      'strong',
      'b',
      'em',
      'i',
      'u',
      's',
      'a',
      'sub',
      'sup',
      'span',
      'div',
      'blockquote',
      'ul',
      'ol',
      'li',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'article',
      'figure',
      'figcaption',
      'img',
      'table',
      'colgroup',
      'col',
      'tbody',
      'tr',
      'td',
      'math',
      'semantics',
      'annotation',
      'mrow',
      'mi',
      'mn',
      'mo',
      'mtext',
      'mspace',
      'msup',
      'msub',
      'msubsup',
      'mfrac',
      'msqrt',
      'mroot',
      'mover',
      'munder',
      'munderover',
      'mtable',
      'mtr',
      'mtd',
      'mpadded',
      'mphantom',
      'menclose',
    ],
    ALLOWED_ATTR: [
      'class',
      'style',
      'value',
      'data-callout',
      'data-type',
      'data-book-image-id',
      'data-book-indent',
      'src',
      'alt',
      'loading',
      'xmlns',
      'encoding',
      'aria-hidden',
      'mathvariant',
      'stretchy',
      'fence',
      'separator',
      'accent',
      'accentunder',
      'columnalign',
      'rowspacing',
      'columnspacing',
      'scriptlevel',
      'displaystyle',
      'width',
      'height',
      'rowspan',
      'colspan',
      'href',
      'target',
      'rel',
    ],
  });
};

const SafeRichContent = ({
  content,
  images = [],
  contentLanguage = 'fr',
  tables = [],
  className = '',
  onImageClick,
}) => {
  const html = useMemo(
    () => buildSafeRichHtml(content, images, contentLanguage, tables),
    [content, images, contentLanguage, tables]
  );

  const handleClick = (event) => {
    const imageElement = event.target.closest?.(
      'img[data-book-image-id]'
    );
    if (!imageElement || !onImageClick) return;
    const image = findInlineImage(
      images,
      imageElement.getAttribute('data-book-image-id'),
      contentLanguage
    );
    if (image) onImageClick(image);
  };

  return (
    <div
      className={`book-rich-content book-list-markers-visible ${className}`.trim()}
      onClick={handleClick}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

export default SafeRichContent;
